import { useState, type ReactNode } from "react";
import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import { orderStatusOptions } from "../../shared/utility";
import ImportMediaCsvDropzone from "../../components/importMedia/ImportMediaCsvDropzone";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { ApiMessage, SelectOption } from "../../types/api";
import type { FetchOrdersPayload, OrderFilters, OrderListItem, OrdersResponse } from "../../types/orders";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import { getSalesOrderTitle } from "../../shared/orderNumbers";

interface UploadEvent { target: { name: string; value: File[] } }
const defaultFilters = (): OrderFilters => ({
  customer: { value: -1, label: "All Customers" },
  orderStatus: { value: -1, label: "All Orders" },
  employee: { value: -1, label: "All Employees" },
  startDate: null,
  endDate: null,
});

export function ManageOrdersViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [customers, setCustomers] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [filters, setFilters] = useState<OrderFilters>(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({ open: false, childComponent: null });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrdersResponse>({
    current_page: 1, data: [], last_page: 1, per_page: 20, total: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [slideOver, setSlideOver] = useState<SlideOverState>({ childComponent: null, open: false });

  const closeModal = () => setEmptyModelOpen({ childComponent: null, open: false });
  const showErrorModal = (title = "Unable to complete request", subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: <ModelErrorComponent setOpen={closeModal} subtitle={subtitle} title={title} />,
      open: true,
    });
  };

  const getOrders = async (search: string | null, page: number, filterArg: OrderFilters) => {
    setLoading(true);
    const payload: FetchOrdersPayload = { page, filters: { ...filterArg, search } };
    const result = await makePostApiCall<OrdersResponse, FetchOrdersPayload>("fetchOrders", payload);
    if (result.success) setOrders(result.data);
    else {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data.message);
    }
    setLoading(false);
  };

  const deleteOrder = async (id: number) => {
    const result = await makePostApiCall<ApiMessage>(`/deleteOrder/${id}`);
    if (result.success) {
      setOrders((current) => ({ ...current, data: current.data.filter((order) => order.id !== id) }));
      closeModal();
      showSuccess(result.data.message || "Action completed");
    } else {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data.message);
    }
  };

  const askDeleteOrder = (order: OrderListItem) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteOrder(order.id)}
          message={`Are you sure you want to delete ${getSalesOrderTitle(order)}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getCustomersForDropdown = async () => {
    const result = await makePostApiCall<{ customers: SelectOption[] }>("getCustomersForDropdown");
    if (result.success) setCustomers(result.data.customers);
    else {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data.message);
    }
  };

  const getUsersForDropdown = async () => {
    const result = await makePostApiCall<SelectOption[]>("usersForDropdown");
    if (result.success) setEmployees(result.data);
    else {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data.message);
    }
  };

  const resetFilters = () => {
    const reset = defaultFilters();
    setFilters(reset);
    setShowFilters(false);
    void getOrders(null, 1, reset);
  };

  const uploadOrderCsv = async (uploadedFile: UploadEvent) => {
    const { name, value } = uploadedFile.target;
    const result = await makePostApiCall<ApiMessage, Record<string, File[]>>(
      "orders/import", { [name]: value }, "multipart/form-data",
    );
    if (result.success) void getOrders(null, 1, filters);
    else {
      if (result.errorStatus === 401) logout();
      showErrorModal(result.data.message);
    }
  };

  const openImportOrder = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="orderCsv"
          actionCall={uploadOrderCsv}
          setOpen={setEmptyModelOpen}
          title="Upload CSV to import orders"
        />
      ),
      open: true,
    });
  };

  const filterComponents: ReactNode[] = [
    <DateRangeFilter
      key="dates"
      endDate={filters.endDate}
      onChange={(name, value) => setFilters((current) => ({ ...current, [name]: value }))}
      resetFilter={() => setFilters((current) => ({ ...current, startDate: null, endDate: null }))}
      startDate={filters.startDate}
    />,
  ];

  if (Number(localStorage.getItem("userType")) === 1) {
    filterComponents.push(
      <FilterSingleSelectDropdown
        key="status" header="Order Status"
        onChange={(value) => setFilters((current) => ({ ...current, orderStatus: value ?? defaultFilters().orderStatus }))}
        options={orderStatusOptions}
        resetFilter={() => setFilters((current) => ({ ...current, orderStatus: defaultFilters().orderStatus }))}
        valueName="orderStatus" value={filters.orderStatus}
      />,
      <FilterSingleSelectDropdown
        key="customer" header="Customer"
        onChange={(value) => setFilters((current) => ({ ...current, customer: value ?? defaultFilters().customer }))}
        options={customers}
        resetFilter={() => setFilters((current) => ({ ...current, customer: defaultFilters().customer }))}
        valueName="customer" value={filters.customer}
      />,
      <FilterSingleSelectDropdown
        key="employee" header="Employee"
        onChange={(value) => setFilters((current) => ({ ...current, employee: value ?? defaultFilters().employee }))}
        options={employees}
        resetFilter={() => setFilters((current) => ({ ...current, employee: defaultFilters().employee }))}
        valueName="employee" value={filters.employee}
      />,
    );
  }

  return {
    filterComponents, getCustomersForDropdown, getUsersForDropdown, getOrders, loading, orders,
    setEmptyModelOpen, setSlideOver, slideOver, emptyModal, askDeleteOrder, filters, resetFilters,
    setShowFilters, showFilters, openImportOrder,
  };
}
